import { useLocation } from "wouter";
import { z } from "zod";
import { Project } from "@/api/studio/projects/types/project.type";
import { Button } from "@/components/ui/button";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useDeleteProject } from "@/api/studio/projects/queries";
import { Form } from "@/components/ui/form/form";
import { TextInput } from "@/components/ui/form/text-input";
import { useModalError } from "@/hooks/use-modal-error";

/* Prop Types */
interface DeleteProjectProps {
  project: Project;
}

/* Schema */
const deleteProjectSchema = z.object({
  confirmation: z.string().refine((val) => val === "DELETE", {
    message: "You must enter DELETE to confirm",
  }),
});

/*
 * DeleteProjectModal component
 * This component is used to delete a project - we provide a confirmation input to ensure the user wants to delete the project
 */
export const DeleteProjectModal = ({ project }: DeleteProjectProps) => {
  /* Stores */
  const [, navigate] = useLocation();

  /* Queries */
  const { mutateAsync: deleteProject, isPending: isDeleteProjectPending, isError: isDeleteProjectError } = useDeleteProject();

  /* Hooks */
  const { close } = useModalError({ error: isDeleteProjectError });

  /* Handlers */
  const handleDelete = async () => {
    await deleteProject(project.id);
    navigate("/projects");
    close();
  };

  /* Render */
  return (
    <Form schema={deleteProjectSchema} onSubmit={handleDelete}>
      {({ register, formState: { errors, isValid } }) => (
        <>
          {/* Header */}
          <div className="flex items-center justify-between pb-4">
            <h2 className="font-semibold">Delete project</h2>
            <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
          </div>

          {/* Details */}
          <div className="pb-4">
            <p className="">Are you sure you want to delete this project?</p>
            <p>This action is immediate and cannot be undone.</p>
            <p>All studies and media in the project will also be deleted.</p>
          </div>

          {/* Confirmation Input */}
          <TextInput
            name="confirmation"
            registration={register("confirmation")}
            label="Type DELETE to confirm"
            disabled={isDeleteProjectPending}
            error={errors.confirmation?.message}
            className="mb-4"
          />

          {/* Buttons */}
          <div className="flex justify-end pt-4">
            <Button type="submit" variant="error" disabled={isDeleteProjectPending || !isValid}>
              {isDeleteProjectPending ? "Deleting..." : "Delete project"}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};
